import { Alert, Button, InputGroup } from "react-bootstrap";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../../providers/authprovider";
import AddEditEventModal from "./AddEditEventModal";
import findColorForUser from "./CalendarUtils";
import Loading from "../../components/LoadingInfo";
import ResponseAlert from "../../components/ResponseAlert";
import DateRangeSlider from "./DateRangeSlider";
import HistoryModal from "./HistoryModal";
import { v4 as uuidv4 } from "uuid";
import Event from "./Event";
import AddEditNoteModal from "./AddEditNoteModal";
import CustomEvent from "./CustomEvent";
import MultiSelect from "../../components/MultiSelect";
import SearchEvents from "./SearchEvents";
import { Route, Routes, useSearchParams } from "react-router-dom";
import SearchBuildingEventsBar from "./SearchBuildingEventsBar";
import { getUserInitials } from "../../utils/utils";
import { useMobileMode } from "../../utils/useMobile";
import CalendarEventsByStatusAndDate from "./components/CalendarEventsByStatusAndDate ";
import { useNavigate } from "react-router-dom";
import CUSTOM_EVENT_TYPE from "./CustomEventType";
import useCalendarStream, { STREAM_STATUS } from "../../utils/useCalendarStream";

const SYNC_STATUS = {
  ...STREAM_STATUS,
  SYNCING: "syncing",
};

const SYNC_STATUS_VIEW = {
  [SYNC_STATUS.LIVE]: {
    label: "na żywo",
    className: "text-success",
    color: "var(--bs-success)",
    title: "Zmiany innych użytkowników pojawiają się na bieżąco",
  },
  [SYNC_STATUS.SYNCING]: {
    label: "synchronizacja…",
    className: "text-primary",
    color: "var(--bs-primary)",
    spin: true,
    title: "Trwa pobieranie wydarzeń",
  },
  [SYNC_STATUS.CONNECTING]: {
    label: "łączenie…",
    className: "text-warning",
    color: "var(--bs-warning)",
    spin: true,
    title: "Trwa nawiązywanie połączenia ze strumieniem zmian",
  },
  [SYNC_STATUS.OFFLINE]: {
    label: "offline",
    className: "text-danger",
    color: "var(--bs-danger)",
    title: "Brak połączenia ze strumieniem zmian — dane mogą być nieaktualne",
  },
  [SYNC_STATUS.DISABLED]: {
    label: "—",
    className: "text-muted",
    color: "var(--bs-gray-500)",
    title: "Strumień zmian nieaktywny",
  },
};

const CONTEXT = "calendar";
const DataContext = React.createContext();

// function isEqualDate(date1,date2){
//     return date1.toLocaleDateString() === date2.toLocaleDateString();
// }

function isDateBetween(date, start, to) {
  let dateToCheck = [date.getFullYear(), date.getMonth() + 1, date.getDate()];

  let checkTimeStamp = Date.parse(dateToCheck.join("-"));
  let startTimestamp = Date.parse(start.join("-"));
  let endTimeStamp = Date.parse(to.join("-"));

  return !(checkTimeStamp < startTimestamp || checkTimeStamp > endTimeStamp);
}

function CalendarDataContainer({ children }) {
  const [shortBuildings, setShortBuildings] = useState(undefined);
  const [teams, setTeams] = useState(undefined);
  const [workers, setWorkers] = useState(undefined);
  const {
    buildings,
    teams: { get: getTeams },
    users,
    calendar,
    user,
  } = useAuth();
  const [alert, setAlert] = useState({ show: false, error: "" });
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // ostatnio żądany zakres dat — resync musi wiedzieć, co przeładować
  const rangeRef = useRef(undefined);
  // stan w refie, bo loadEvents musi widzieć aktualną wartość poza cyklem renderu
  const loadingRef = useRef(false);

  function setWorkerColors(workers) {
    workers.forEach(
      (worker) => (worker.color = findColorForUser(worker.id, teams)),
    );

    return workers;
  }

  const loadEvents = (startDate, endDate) => {
    const range = { startDate, endDate };
    const sameRange =
      rangeRef.current?.startDate === startDate &&
      rangeRef.current?.endDate === endDate;

    // Żądanie tego samego zakresu, gdy poprzednie jeszcze leci, nic nie wnosi —
    // a przy zrywającym się strumieniu potrafiłoby zalać serwer połączeniami.
    if (loadingRef.current && sameRange) return;

    rangeRef.current = range;
    loadingRef.current = true;
    setEventsLoading(true);

    calendar.load(startDate, endDate, (response) => {
      const { success, error, content } = response;

      loadingRef.current = false;
      setEventsLoading(false);

      if (success === false) return setAlert({ show: true, error: error });

      setEvents(content);
    });
  };

  // wariant funkcyjny jest konieczny, bo zdarzenia ze strumienia przychodzą
  // asynchronicznie — dwa w jednym ticku pracowałyby na tym samym stanie z domknięcia
  const addEvent = (event) => {
    setEvents((prev) => {
      const eventPos = prev.findIndex((e) => e.id === event.id);

      if (eventPos === -1) return [...prev, event];

      const next = [...prev];
      next.splice(eventPos, 1, event);

      return next;
    });
  };

  const deleteEvent = (event) => {
    setEvents((prev) => prev.filter((e) => e.id !== event.id));
  };

  const handleStreamChange = useCallback(
    (payload) => {
      const { action, event, actorId } = payload;

      // własne zmiany są już w stanie (optymistyczna aktualizacja) — pomijamy, żeby nie migotało
      if (actorId === user?.id) return;

      if (action === "delete") return deleteEvent(event);

      addEvent(event);
    },
    [user?.id],
  );

  const handleStreamResync = useCallback(() => {
    const range = rangeRef.current;

    if (range) loadEvents(range.startDate, range.endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const streamStatus = useCalendarStream(
    Boolean(user),
    handleStreamChange,
    handleStreamResync,
  );

  // pobieranie wydarzeń przykrywa stan strumienia — użytkownika interesuje wtedy
  // to, że dane właśnie się dociągają, a nie że połączenie stoi
  const syncStatus = eventsLoading ? SYNC_STATUS.SYNCING : streamStatus;

  useEffect(() => {
    if (shortBuildings === undefined) {
      buildings.getShort(({ success, error, content }) => {
        if (success === true) return setShortBuildings(content);

        setShortBuildings([]);
        setAlert({ show: true, error: error });
      });
      return;
    }

    if (teams === undefined) {
      getTeams(({ success, error, content }) => {
        if (success === true) return setTeams(content);

        setTeams([]);
        setAlert({ show: true, error: error });
      }, CONTEXT);
      return;
    }

    if (workers === undefined) {
      users.get(({ success, error, content }) => {
        if (success === true) return setWorkers(setWorkerColors(content));

        setWorkers([]);
        setAlert({ show: true, error: error });
      }, CONTEXT);
    }
  });

  const DATA_LOADED =
    shortBuildings !== undefined &&
    workers !== undefined &&
    teams !== undefined;

  const value = {
    shortBuildings: shortBuildings,
    teams: teams,
    workers: workers,
    loadEvents: loadEvents,
    events: events,
    handleSuccessAddEdit: addEvent,
    handleDeleteEvent: deleteEvent,
    syncStatus: syncStatus,
  };

  return (
    <>
      <ResponseAlert show={alert.show} error={alert.error} />
      {DATA_LOADED ? (
        <DataContext.Provider value={value}>{children}</DataContext.Provider>
      ) : (
        <Loading />
      )}
    </>
  );
}

export default function Calendar(props) {
  return (
    <CalendarDataContainer>
      <CalendarRoutes privilege={props.privilege} />
    </CalendarDataContainer>
  );
}

export function useData() {
  return useContext(DataContext);
}

function CalendarRoutes({ privilege }) {
  return (
    <Routes>
      <Route path={"/"} element={<CalendarManager privilege={privilege} />} />
      <Route
        path={"/selectWithStatusAndToDate"}
        element={<CalendarEventsByStatusAndDate privilege={privilege} />}
      />
      <Route
        path={"/:buildingId"}
        element={<SearchEvents privilege={privilege} />}
      />
    </Routes>
  );
}

function CalendarManager(props) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { privilege } = props;
  const { user } = useAuth();
  const isStandalone = useMobileMode();
  const {
    shortBuildings,
    teams,
    workers,
    events,
    loadEvents,
    handleSuccessAddEdit,
    handleDeleteEvent,
    syncStatus,
  } = useData();
  const SPREAD_DAYS = 14;
  const [timestamp, setTimestamp] = useState(() => {
    const localDate = searchParams.get("date");
    const date = new Date();
    if (localDate) {
      date.setTime(Date.parse(localDate));
    } else {
      const weekDay = date.getDay() - 1;
      const monthDay = date.getDate();
      date.setDate(monthDay - weekDay);
    }

    loadEvents(date.getTime(), date.getTime() + 14 * 24 * 3600 * 1000);
    return date.getTime();
  });
  const [usersFilter, setUsersFilter] = useState([]);
  const [showOnlyCustomEvents, setShowOnlyCustomEvents] = useState(false);
  const [showOnlyVacationEvents, setShowOnlyVacationEvents] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState({
    show: false,
    note: undefined,
    type: undefined,
  });
  const [addEditModal, setAddEditModal] = useState({
    show: false,
    date: undefined,
    event: undefined,
  });
  const serviceTechnicians = useMemo(() => {
    return filterWorkersWithServiceRole(workers);
  }, [workers]);

  const weekRows = useMemo(() => {
    const days = [];
    const startDate = new Date();
    let endDate = undefined;
    startDate.setTime(timestamp);
    const startDay = startDate.getDate();

    for (let i = 0; i < SPREAD_DAYS; i++) {
      endDate = new Date(startDate);
      endDate.setDate(startDay + i);
      days.push({ id: i, date: endDate });
    }
    startDate.setDate(startDate.getDate() - 1);

    let rows = createWeekRows(days);
    let groups = groupEventsInRows(rows);

    return groups;
  }, [timestamp, events]);

  function createWeekRows(days) {
    let dayDates = [[], []];

    days.forEach((d, index) => {
      let row = Math.floor(index / 7);
      dayDates[row].push({ date: d.date, events: [] });
    });

    return dayDates;
  }

  function groupEventsInRows(weekRows) {
    events?.forEach((event) => {
      const date = isCustomEvent(event) ? event.date_from : event.date;

      const start_date = date
        .split("T")[0]
        .split("-")
        .map((v) => Number(v));
      const end_date = event.date_to
        .split("T")[0]
        .split("-")
        .map((v) => Number(v));

      for (let row = 0; row < weekRows.length; row++) {
        for (let i = 0; i < weekRows[row].length; i++) {
          const day = weekRows[row][i];

          if (!isDateBetween(day.date, start_date, end_date)) continue;

          day.events.push(event);
          break;
        }
      }
    });
    return weekRows;
  }

  const handleDateChange = (date) => {
    setTimestamp(date.getTime());
    loadEvents(date.getTime(), date.getTime() + 14 * 24 * 3600 * 1000);
  };

  const handleAddEditEvent = (date, event) => {
    setAddEditModal({ show: true, date: date, event: event });
  };

  const handleEditCustomEvent = (event) => {
    setShowNoteModal({ show: true, note: event, type: event.type });
  };

  const handleHideAddEditModal = () => {
    setAddEditModal({ show: false, date: undefined, event: undefined });
  };

  const handleShowHideHistoryModal = (show) => {
    setShowHistoryModal(show);
  };

  const showAddEditCustomEventModal = (type) => {
    setShowNoteModal({ show: true, note: undefined, type });
  };

  const createCalendarMap = (from, to) => {
    const weekNum = Math.floor(from / 7);
    const weekDays = weekRows[weekNum];

    const elements = [];
    for (let i = from; i < to; i++) {
      const dayPos = (i % 7) + 1;
      const day = weekDays[i % 7];
      const dayEvents = day.events;
      const usersInDay = getWorkersForDateFromEvents(
        day.date,
        weekDays.flatMap((d) => d.events),
      );
      const technicansNotInDay = serviceTechnicians.filter(
        (w) => !usersInDay.includes(w.id),
      );

      elements.push(
        <div
          key={day.date.toLocaleDateString()}
          className={"d-flex justify-content-between calendarDayDate"}
        >
          <div className="calendarDayWorkers ">
            {!isStandalone &&
              technicansNotInDay?.map((user) => (
                <span
                  key={user.id}
                  className={"userBadge m-0 ps-1 pe-1 cursor-pointer"}
                  style={{ backgroundColor: user.color }}
                  title={user.name}
                >
                  {getUserInitials(user)}
                </span>
              ))}
          </div>
          <div className="fs-6 text-end">
            {day.date.toLocaleDateString()}
            <Button
              className={"rounded-3 float-end mt-1 ms-1"}
              size={"sm"}
              onClick={() => {
                handleAddEditEvent(day.date, undefined);
              }}
              variant={"outline-secondary"}
            >
              +
            </Button>
            <div className={"text-muted small"}>
              {getDayOfWeak(day.date.getDay())}
            </div>
          </div>
        </div>,
      );

      for (let x = 0; x < dayEvents.length; x++) {
        const event = dayEvents[x];

        if (isCustomEvent(event)) {
          const showAllCustomEventTypes =
            showOnlyCustomEvents === false && showOnlyVacationEvents === false;
          const isNote = event.type === CUSTOM_EVENT_TYPE.NOTE;
          const isVacation = event.type === CUSTOM_EVENT_TYPE.VACATION;
          const shouldShowByType =
            showAllCustomEventTypes ||
            (showOnlyCustomEvents && isNote) ||
            (showOnlyVacationEvents && isVacation);

          if (!shouldShowByType) continue;

          if (!isVacation && !isUserIncluded(event, usersFilter))
            continue;

          elements.push(
            <CustomEvent
              key={uuidv4()}
              event={event}
              day={dayPos}
              currentdate={day.date}
              onClick={handleEditCustomEvent}
            />,
          );
        } else if (
          showOnlyCustomEvents === false &&
          showOnlyVacationEvents === false
        ) {
          if (!isUserIncluded(event, usersFilter)) continue;

          elements.push(
            <Event
              key={uuidv4()}
              event={event}
              day={dayPos}
              currentdate={day.date}
              privilege={privilege}
              onClick={() => {
                handleAddEditEvent(day.date, event);
              }}
              onDelete={handleDeleteEvent}
            />,
          );
        }
      }
    }
    return <div className={"calendarMap"}>{elements}</div>;
  };

  const selectWorkersForFiltering = () => {
    if (privilege.edit)
      return workers?.map((w) => {
        return { value: w.id, name: w.name };
      });
    else return [{ value: user.id, name: user.name }];
  };

  const setPersonFilter = (ids) => {
    setUsersFilter([...ids]);
  };

  const handleChangeFilterCustomEventCheckbox = (event) => {
    setShowOnlyCustomEvents(event.target.checked);
  };

  const handleChangeFilterVacationEventCheckbox = (event) => {
    setShowOnlyVacationEvents(event.target.checked);
  };

  const info =
    timestamp < new Date(2022, 10, 3).getTime() ? (
      <Alert className={"mt-4"} variant={"info"}>
        Ważne: z uwagi na zmianę działania systemu aby zakończyć poprawnie
        przegląd sprzed dnia 03.11.2022r należy ustawić im status "ZAKOŃCZENIE".{" "}
        <br /> Wydarzenia od dnia 03.11.2022r (włącznie) będą generować kolejne
        gdy zostanie ustawiony im status "REALIZACJA"{" "}
      </Alert>
    ) : (
      <></>
    );

  return (
    <div className={"calendarContainer"}>
      {info}
      <div className={"calendarBottomBar"}>
        {/* belka ma justify-content: flex-end, więc wskaźnik odpychamy w lewo marginesem */}
        <SyncStatusIndicator status={syncStatus} />

        <Button
          variant={"outline-info"}
          onClick={() => {
            handleShowHideHistoryModal(true);
          }}
        >
          Historia
        </Button>

        <div>
          <InputGroup className="customEventCheckboxContainer">
            <Button
              variant={"outline-primary"}
              onClick={() => {
                showAddEditCustomEventModal(CUSTOM_EVENT_TYPE.NOTE);
              }}
            >
              Nowa notatka
            </Button>
            <InputGroup.Checkbox
              aria-label="Checkobox for filtering custom events"
              checked={showOnlyCustomEvents}
              onChange={handleChangeFilterCustomEventCheckbox}
            />
          </InputGroup>
        </div>
        {(user.isAdmin || privilege.edit) && (
          <div>
            <InputGroup className="customEventCheckboxContainer vacationEventCheckboxContainer">
              <Button
                variant={"outline-warning"}
                onClick={() => {
                  showAddEditCustomEventModal(CUSTOM_EVENT_TYPE.VACATION);
                }}
              >
                Urlop
              </Button>
              <InputGroup.Checkbox
                aria-label="Checkobox for filtering vacations"
                checked={showOnlyVacationEvents}
                onChange={handleChangeFilterVacationEventCheckbox}
              />
            </InputGroup>
          </div>
        )}
      </div>
      <div className="position-absolute w-25 p-1 d-flex align-items-start gap-2">
        <div className="flex-grow-1">
          <MultiSelect
            elements={selectWorkersForFiltering()}
            title={"Filtruj osoby"}
            onChange={setPersonFilter}
          />
        </div>
        {privilege.edit && (
          <Button
            variant="outline-secondary"
            onClick={() => {
              navigate("/calendar/selectWithStatusAndToDate");
            }}
          >
            do&nbsp;realizacji
          </Button>
        )}
      </div>

      <div
        className={"position-absolute"}
        style={{ right: "1rem", top: "4px" }}
      >
        <SearchBuildingEventsBar />
      </div>

      <DateRangeSlider timestamp={timestamp} onChangeDate={handleDateChange} />
      {createCalendarMap(0, 7)}
      {createCalendarMap(7, 14)}
      {addEditModal.show && (
        <AddEditEventModal
          privilege={privilege}
          show={true}
          event={addEditModal.event}
          onHide={handleHideAddEditModal}
          onSuccess={handleSuccessAddEdit}
          buildings={shortBuildings}
          teams={teams}
          workers={workers}
          date={addEditModal.date}
        />
      )}
      {showHistoryModal && (
        <HistoryModal
          show={showHistoryModal}
          onHide={() => handleShowHideHistoryModal(false)}
        />
      )}
      {showNoteModal.show && (
        <AddEditNoteModal
          show={true}
          event={showNoteModal.note}
          eventType={showNoteModal.type}
          onHide={() => {
            setShowNoteModal({ show: false, note: undefined, type: undefined });
          }}
          teams={teams}
          workers={workers}
          onSave={handleSuccessAddEdit}
        />
      )}
    </div>
  );
}

function SyncStatusIndicator({ status }) {
  const view = SYNC_STATUS_VIEW[status] || SYNC_STATUS_VIEW[SYNC_STATUS.OFFLINE];

  return (
    <span
      className={`small me-auto ${view.className}`}
      title={view.title}
      style={{ cursor: "help", whiteSpace: "nowrap" }}
    >
      <span
        className={view.spin ? "spinner-grow spinner-grow-sm" : ""}
        style={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: "50%",
          marginRight: ".35rem",
          backgroundColor: view.color,
        }}
      />
      {view.label}
    </span>
  );
}

function isCustomEvent(event) {
  return event.type === undefined || event.date === undefined;
}

function isUserIncluded(event, userIds) {
  if (userIds.length <= 0) return true;

  if (isCustomEvent(event))
    return event.users.findIndex((u) => userIds.includes(u.id)) > -1;

  return event.users.findIndex((id) => userIds.includes(id)) > -1;
}

function getDayOfWeak(day) {
  const days = [
    "Niedziela",
    "Poniedziałek",
    "Wtorek",
    "Środa",
    "Czwartek",
    "Piątek",
    "Sobota",
  ];
  return days[day];
}

function getWorkersForDateFromEvents(date, events) {
  const users = new Set();
  events
    .filter((e) => {
      const start_date = (e.date || e.date_from)
        .split("T")[0]
        .split("-")
        .map((v) => Number(v));
      const end_date = e.date_to
        .split("T")[0]
        .split("-")
        .map((v) => Number(v));
      return isDateBetween(date, start_date, end_date);
    })
    .forEach((event) => {
      if (isCustomEvent(event)) {
        event.users?.forEach((user) => users.add(user.id));
        return;
      } else {
        event.users?.forEach((user) => users.add(user));
      }
    });
  return Array.from(users);
}

function filterWorkersWithServiceRole(workers) {
  const serviceRole = "Serwisant";
  //make it case insensitive
  return workers.filter((worker) => {
    return worker.position?.toLowerCase() === serviceRole.toLowerCase();
  });
}
